import React, { useState } from "react";
import {
  Box, Button, Chip, CircularProgress, IconButton, Link, Paper,
  TextField, Tooltip, Typography
} from "@mui/material";
import { useSelector } from "react-redux";
import moment from "moment";
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import UndoIcon from '@mui/icons-material/Undo';
import CommentIcon from '@mui/icons-material/Comment';
import LinkIcon from '@mui/icons-material/Link';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';


import { DeleteFile, UploadFile } from "../../services/backend/UploadFileService";
import { assertPdfNotEncrypted } from "../../services/pdf/validatePdf";
import { genString, getActivityStatus } from "../../functions";
import { REVIEW_STATUS, REVIEW_STATUS_UI } from "../../constants";
import ReviewStatusChip from "../generic/ReviewStatusChip";
import ChargeAccountAnnexeModal from "./ChargeAccountAnnexeModal";

const ChargeAccountActivity = ({
  idx,
  title,
  description,
  annexes,
  rejected,
  reviewStatus,
  returns,
  contractCode,
  updateActivity,
  removeActivity,
  editing,
  readOnly,
  setEditing,
  payment,
  setInfoSnack,
  canReturn,
  canRemove,
  isAddedInCycle,
  onReturnActivity,
  onUndoReturn
}) => {
  const user = useSelector((state) => state.user.value);
  const [loading, setLoading] = useState(false);
  const [showAnnexeModal, setShowAnnexeModal] = useState(false);

  const hasDescription = Boolean(description && description.trim());
  const annexCount = annexes?.length || 0;
  const status = getActivityStatus({ rejected, reviewStatus });
  const isReturned = status === REVIEW_STATUS.RETURNED;
  const isApproved = status === REVIEW_STATUS.APPROVED;

  const handleUploadAnnexed = async (evt) => {
    const file = evt.target.files[0];
    if (!file) return;

    const encryptedError = await assertPdfNotEncrypted(file);
    if (encryptedError) {
      evt.target.value = '';
      if (setInfoSnack) {
        setInfoSnack({ open: true, message: encryptedError, severity: 'error', duration: 5000 });
      }
      return;
    }

    setLoading(true);
    const ext = file.name.split('.').at(-1);
    const name = genString();

    const response = await UploadFile(
      user,
      file,
      process.env.REACT_APP_STORAGE_BUCKET_CONTRACTS,
      `${contractCode}/annexe_${name}.${ext}`
    );

    if (response?.result) {
      updateActivity(idx, { annexes: [ ...annexes, {
        id: `${name}.${ext}`,
        name: file.name,
        url: response.downloadUrl
      } ]});
    }

    setLoading(false);
  };

  const handleDeleteAnnexe = async (annIdx) => {
    const ann = [...annexes];
    const delAnn = ann.splice(annIdx, 1);
    updateActivity(idx, { annexes: ann });
    DeleteFile(
      user,
      process.env.REACT_APP_STORAGE_BUCKET_CONTRACTS,
      `${contractCode}/annexe_${delAnn[0].id}`
    );
  };

  // El botón de devolver aparece para el revisor mientras la actividad no
  // esté devuelta ni aprobada; la observación se registra en el diálogo.
  const showReturnAction = canReturn && !isReturned && !isApproved && onReturnActivity;
  // El deshacer ocupa el mismo slot, sobre la actividad Devuelta. `canReturn`
  // ya excluye el mes "En corrección": no se deshace una devolución notificada.
  const showUndoAction = canReturn && isReturned && onUndoReturn;

  const statusChip = isReturned || isApproved
    ? <ReviewStatusChip status={status} />
    : hasDescription
        ? <Chip
            size="small"
            icon={<CheckCircleIcon />}
            label="Con descripción"
            color="success"
            variant="outlined"
          />
        : <Chip
            size="small"
            icon={<RadioButtonUncheckedIcon />}
            label="Sin descripción"
            variant="outlined"
          />;

  // Historial de devoluciones de esta actividad (autor, fecha, observación).
  // Las deshechas se conservan, atenuadas y con la marca de quién las deshizo.
  const returnsChip = returns?.length > 0
    ? <Tooltip
        title={(
          <Box sx={{ p: 0.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 0.5 }}>
              Devoluciones ({returns.length})
            </Typography>
            {returns.map((ret, rIdx) => (
              <Box key={rIdx} sx={{ mb: 0.5, opacity: ret.undone ? 0.6 : 1 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    textDecoration: ret.undone ? 'line-through' : 'none',
                  }}
                >
                  {ret.name} — {moment(ret.at).format('YYYY-MM-DD HH:mm')}
                </Typography>
                <Typography sx={{ fontSize: 12, textDecoration: ret.undone ? 'line-through' : 'none' }}>
                  {ret.note}
                </Typography>
                {ret.undone && (
                  <Typography sx={{ fontSize: 12, fontStyle: 'italic' }}>
                    Deshecha por {ret.undone.name} — {moment(ret.undone.at).format('YYYY-MM-DD HH:mm')}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      >
        <Chip
          size="small"
          icon={<CommentIcon />}
          label={`${returns.length} devolución${returns.length > 1 ? 'es' : ''}`}
          color={REVIEW_STATUS_UI[REVIEW_STATUS.RETURNED].color}
          variant="outlined"
        />
      </Tooltip>
    : null;

  const annexChip = annexCount > 0
    ? <Chip
        size="small"
        icon={<AttachFileIcon />}
        label={`${annexCount} anexo${annexCount > 1 ? 's' : ''}`}
        variant="outlined"
      />
    : null;

  // Marca de origen (no de revisión): actividad agregada por el contratista en
  // el ciclo de corrección vigente/último. Permite a la revisión distinguir lo
  // que no formaba parte del conjunto revisado originalmente; desaparece con
  // la aprobación del acta.
  const addedChip = isAddedInCycle && payment?.approved !== true
    ? <Tooltip title="Agregada por el contratista durante la corrección del mes">
        <Chip
          size="small"
          icon={<AddCircleOutlineIcon />}
          label="Nueva en corrección"
          color="info"
          variant="outlined"
        />
      </Tooltip>
    : null;

  if (!editing) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderColor: isReturned ? `${REVIEW_STATUS_UI[REVIEW_STATUS.RETURNED].color}.main` : 'divider',
          borderLeft: '3px solid',
          borderLeftColor: isReturned
            ? `${REVIEW_STATUS_UI[REVIEW_STATUS.RETURNED].color}.main`
            : hasDescription ? 'success.main' : 'action.disabled'
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={title} placement="top-start">
            <Typography
              sx={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {title}
            </Typography>
          </Tooltip>
          <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
            {statusChip}
            {addedChip}
            {annexChip}
            {returnsChip}
          </Box>
        </Box>

        {showReturnAction && (
          <Tooltip title="Devolver actividad (con observación)">
            <IconButton size="small" onClick={() => onReturnActivity(idx)}>
              <AssignmentReturnIcon color={REVIEW_STATUS_UI[REVIEW_STATUS.RETURNED].color} />
            </IconButton>
          </Tooltip>
        )}
        {showUndoAction && (
          <Tooltip title="Deshacer devolución (vuelve a Pendiente)">
            <IconButton size="small" onClick={() => onUndoReturn(idx)}>
              <UndoIcon color={REVIEW_STATUS_UI[REVIEW_STATUS.RETURNED].color} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={readOnly ? "Ver detalle" : "Editar detalle y anexos"}>
          <IconButton onClick={() => setEditing(idx)}>
            {readOnly
              ? <VisibilityIcon />
              : <EditNoteIcon color={hasDescription ? "success" : "primary"} />
            }
          </IconButton>
        </Tooltip>
        {/* Eliminar en borrador y, con el mes en corrección, sobre la actividad
            devuelta o la agregada en el ciclo (lo decide el modal vía
            `canRemove`). Fuera del ciclo una actividad enviada se corrige, no
            se borra. */}
        {canRemove && (
          <Tooltip title="Eliminar actividad">
            <IconButton onClick={() => removeActivity(idx)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Paper>
    );
  }

  return (
    <>
      <ChargeAccountAnnexeModal
        open={!!showAnnexeModal}
        onClose={() => setShowAnnexeModal(false)}
        onAddAnnexeURL={(annexe) => {
          updateActivity(idx, { annexes: [...annexes, annexe] });
          setShowAnnexeModal(false);
        }}
      />
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 1,
          position: 'relative',
          bgcolor: '#fffde7',
          borderColor: '#f9e38a',
          overflow: 'hidden'
        }}
      >
        <IconButton
          size="small"
          onClick={() => setEditing(undefined)}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Typography
          sx={{
            fontWeight: 600,
            pr: 5,
            mb: 2,
            wordBreak: 'break-word'
          }}
        >
          {title}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Descripción de la actividad realizada
        </Typography>
        <TextField
          fullWidth
          multiline
          size="small"
          minRows={3}
          placeholder="Describe cómo cumpliste esta obligación durante el período"
          sx={{
            bgcolor: 'background.paper',
            mb: 2.5,
            '& textarea': { wordBreak: 'break-word' }
          }}
          value={description || ''}
          InputProps={{ readOnly }}
          onChange={(evt) => updateActivity(idx, { description: evt.target.value })}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
            Anexos
          </Typography>
          {loading && <CircularProgress size={16} />}
        </Box>

        {!readOnly && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <Button
              component="label"
              variant="outlined"
              size="small"
              startIcon={<AttachFileIcon />}
              disabled={loading}
            >
              Adjuntar archivo
              <input
                hidden
                accept="*"
                type="file"
                onChange={handleUploadAnnexed}
              />
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LinkIcon />}
              disabled={loading}
              onClick={() => setShowAnnexeModal(true)}
            >
              Agregar URL
            </Button>
          </Box>
        )}

        {!readOnly && annexCount === 0 && (
          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              textAlign: 'center',
              bgcolor: 'background.paper'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Aún no has adjuntado anexos. Sube archivos o agrega URLs como evidencia de esta actividad.
            </Typography>
          </Box>
        )}

        {annexCount > 0 && (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
            {annexes.map((annexe, i) => {
              const isUrl = typeof annexe.id === 'string' && annexe.id.endsWith('.url');
              return (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderTop: i > 0 ? '1px solid' : 'none',
                    borderColor: 'divider'
                  }}
                >
                  {isUrl
                    ? <LinkIcon fontSize="small" sx={{ color: 'action.active' }} />
                    : <InsertDriveFileIcon fontSize="small" sx={{ color: 'action.active' }} />
                  }
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Link
                      component="a"
                      href={annexe.url}
                      target="_blank"
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {annexe.name}
                    </Link>
                  </Box>
                  {!readOnly && (
                    <Tooltip title="Eliminar anexo">
                      <IconButton size="small" onClick={() => handleDeleteAnnexe(i)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
    </>
  );
};

export default ChargeAccountActivity;
